import { GqlImageBanner } from 'types/graphql';

/**
 * Deduplicates banners by `_id`, keeping the last occurrence of each id and
 * preserving the array's original ordering. Guards against the backend's
 * delete-all-then-recreate persistence pattern turning a stale Apollo cache
 * read or a double save into duplicated banners.
 */
export function deduplicateBannersById(banners: GqlImageBanner[]): GqlImageBanner[] {
  const seenIds = new Set<string>();
  const deduplicated: GqlImageBanner[] = [];

  for (let i = banners.length - 1; i >= 0; i -= 1) {
    const banner = banners[i];
    if (!banner._id || !seenIds.has(banner._id)) {
      if (banner._id) {
        seenIds.add(banner._id);
      }
      deduplicated.unshift(banner);
    }
  }

  return deduplicated;
}

/**
 * Normalizes image banners by ensuring platforms is always an array
 * and schedule/timeRanges are properly structured for GraphQL mutation.
 * Also limits how many schedules there can be to 7 (days of week)
 */
export function normalizeBannersForUpdate(sortedBanners: GqlImageBanner[]): GqlImageBanner[] {
  return sortedBanners.map((banner) => {
    let timeRanges = banner.schedule?.timeRanges ?? undefined;
    if (timeRanges) {
      // there was a bug where schedules were being duplicated
      // deduplicate by dayOfWeek (keeping first occurrence) and sort ascending
      // this can likely be removed in the future, but is harmless to remain
      const deduplicated = Object.values(
        timeRanges.reduce<Record<number, typeof timeRanges[number]>>((acc, tr) => {
          if (!acc[tr.dayOfWeek]) {
            acc[tr.dayOfWeek] = tr;
          }
          return acc;
        }, {})
      );

      timeRanges = deduplicated.map((tr) => ({
        id: tr.id,
        dayOfWeek: tr.dayOfWeek,
        startTime: tr.startTime,
        endTime: tr.endTime,
      }));
    }

    return {
      ...banner,
      platforms: banner.platforms ?? [],
      schedule: banner.schedule
        ? {
            id: banner.schedule.id,
            startDate: banner.schedule.startDate,
            endDate: banner.schedule.endDate,
            timeRanges,
          }
        : undefined,
    };
  });
}
